import React from 'react';
import { useParams, Link } from 'react-router-dom';
import styles from './Components/CSS/BlogsCommonPage.module.css';
import { useBlog, useSimilarBlogs } from '../hooks/useBlogs';
import Footer from '../common/UI/Footer';
import Loading from '../common/HousGlass Loader/Loading';

const BlogCommonPage = () => {
  const { id } = useParams();
  const { blog, isLoading, error } = useBlog(id);
  const { similarBlogs, isLoading: similarBlogsLoading } = useSimilarBlogs(blog?.id);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Loading/>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>
          <h2>Unable to load article</h2>
          <p>Please try again later</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className={styles.container}>
        <div className={styles.notFound}>
          <h2>Article not found</h2>
          <p>The article you're looking for doesn't exist or has been removed</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <article className={styles.article}>
        <header className={styles.header}>
          <h1 className={styles.title}>{blog.title}</h1>
          <div className={styles.authorSection}>
            <div className={styles.authorviewContainer}>
              <div className={styles.authorAvatar}>
                {blog.author?.name?.charAt(0) || '?'}
              </div>
              <div className={styles.authorInfo}>
                <span className={styles.authorName}>{blog.author?.name || 'Unknown Author'}</span>
                <div className={styles.metrics}>
                  <div className={styles.metric}>
                    <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>{blog.views}</span>
                  </div>
                  <div className={styles.metric}>
                    <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span>{blog.likes}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.meta}>
              <time className={styles.date}>
                {new Date(blog.publishedAt || blog.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          </div>
        </header>
        
        {blog.featuredImage && (
          <figure className={styles.featuredImage}>
            <img src={blog.featuredImage} alt={blog.title} />
          </figure>
        )}
        
        <div
          className={styles.content}
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />
        
        {/* Similar Blogs Section */}
        <div className={styles.similarBlogsSection}>
          <h2 className={styles.similarBlogsTitle}>You May Also Like</h2>
          
          {similarBlogsLoading ? (
            <div className={styles.similarBlogsLoading}>
              <div className={styles.smallSpinner}></div>
            </div>
          ) : similarBlogs && similarBlogs.length > 0 ? (
            <div className={styles.similarBlogsGrid}>
              {similarBlogs.map(blog => (
                <Link to={`/blog/${blog.slug}`} key={blog.id} className={styles.similarBlogCard}>
                  {blog.featuredImage && (
                    <div className={styles.similarBlogImage}>
                      <img src={blog.featuredImage} alt={blog.title} />
                    </div>
                  )}
                  <div className={styles.similarBlogContent}>
                    <h3 className={styles.similarBlogTitle}>{blog.title}</h3>
                    <div className={styles.similarBlogMeta}>
                      <span className={styles.similarBlogCategory}>{blog.category}</span>
                      <div className={styles.similarBlogStats}>
                        <span className={styles.similarBlogViews}>
                          <svg className={styles.smallIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {blog.views}
                        </span>
                      </div>
                    </div>
                    <p className={styles.similarBlogExcerpt}>{blog.content.replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className={styles.noSimilarBlogs}>No similar articles found</p>
          )}
        </div>
      </article>
      <Footer/>
    </div>
  );
};

export default BlogCommonPage;